import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
const control =
  "mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white px-4";
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export default function ReturnReconciliation({ company, reload }) {
  const { accessToken } = useAuth();
  const [date, setDate] = useState(today),
    [registration, setRegistration] = useState(""),
    [registrations, setRegistrations] = useState([]),
    [report, setReport] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    const requests = generation; // Stable request counter, not a DOM ref.
    requests.current++;
    setReport(null);
    setError("");
    setBusy(false);
    setRegistration("");
    setRegistrations([]);
    return () => {
      requests.current++;
    };
  }, [company, accessToken, reload]);
  function invalidate() {
    generation.current++;
    setReport(null);
    setError("");
    setBusy(false);
  }
  async function load(offset = 0) {
    const request = ++generation.current;
    setBusy(true);
    setError("");
    setReport(null);
    try {
      const q = new URLSearchParams({ asOf: date, offset: String(offset) });
      if (registration) q.set("registrationId", registration);
      const result = await apiRequest(
        `/accounting/${company}/return-clearing-reconciliation?${q}`,
        { accessToken },
      );
      if (request !== generation.current) return;
      setReport(result);
      setRegistrations(result.registrations);
    } catch (e) {
      if (request === generation.current) setError(e.message);
    } finally {
      if (request === generation.current) setBusy(false);
    }
  }
  return (
    <details className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6">
      <summary className="cursor-pointer text-xl font-semibold">
        Return-clearing reconciliation
      </summary>
      <p className="text-sm text-gray-500">
        Read-only check of dispatched goods awaiting supplier credit against
        recorded clearing entries. Historical ledger mappings remain visible.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        className="space-y-4"
      >
        <fieldset
          disabled={busy || !company}
          className="grid gap-4 md:grid-cols-2"
        >
          <label>
            Clearing as-of date
            <input
              required
              type="date"
              value={date}
              className={control}
              onChange={(e) => {
                invalidate();
                setDate(e.target.value);
              }}
            />
          </label>
          <label>
            Clearing GST registration
            <select
              value={registration}
              className={control}
              onChange={(e) => {
                invalidate();
                setRegistration(e.target.value);
              }}
            >
              <option value="">All company registrations</option>
              {registrations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} · {r.gstin}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
        <button
          disabled={busy || !company}
          className="h-11 rounded-xl bg-primary-700 px-5 font-medium text-white disabled:opacity-50"
        >
          {busy ? "Loading reconciliation…" : "Review Clearing"}
        </button>
      </form>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {report && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{report.explanation}</p>
          <p
            className={`rounded-xl p-4 text-sm ${report.needsReview ? "bg-amber-50 text-amber-900" : "bg-green-50 text-green-800"}`}
            role="status"
          >
            {report.needsReview
              ? "Review required: differences or unlinked clearing entries exist."
              : "Recorded clearing agrees with linked dispatches for this scope."}{" "}
            No supplier dues, stock or GST balances were changed.
          </p>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Clearing ledger</th>
                  <th>GSTIN</th>
                  <th>Dispatched goods</th>
                  <th>Accepted goods credit</th>
                  <th>Written-off goods</th>
                  <th>Awaiting credit</th>
                  <th>Recorded net Dr − Cr</th>
                  <th>Difference</th>
                  <th>Unlinked entries</th>
                </tr>
              </thead>
              <tbody>
                {report.groups.map((g) => (
                  <tr key={`${g.ledgerId}:${g.registrationId || "none"}`}>
                    <td>
                      {g.ledgerCode} · {g.ledgerName}
                    </td>
                    <td>{g.gstin || "Unregistered — review"}</td>
                    <td>₹{g.dispatchedGoods}</td>
                    <td>₹{g.acceptedGoods || '0.00'}</td>
                    <td>₹{g.writtenOffGoods || '0.00'}</td>
                    <td>₹{g.awaitingGoods || g.dispatchedGoods}</td>
                    <td>₹{g.recordedNet}</td>
                    <td>₹{g.difference}</td>
                    <td>
                      {g.unlinkedLineCount}
                      {g.needsReview ? " · Review" : ""}
                    </td>
                  </tr>
                ))}
                {!report.groups.length && (
                  <tr>
                    <td colSpan={9}>
                      No clearing mappings or recorded activity in this scope.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-sm">
            All matching records: dispatched goods ₹
            {report.totals.dispatchedGoods}; accepted goods ₹{report.totals.acceptedGoods || '0.00'}; written-off goods ₹{report.totals.writtenOffGoods || '0.00'}; awaiting credit ₹{report.totals.awaitingGoods || report.totals.dispatchedGoods}; recorded net ₹
            {report.totals.recordedNet}; difference ₹{report.totals.difference}.
            Review each GST/ledger row; differences must not be netted across
            registrations.
          </p>
          <h3 className="font-semibold">Dispatch and supplier-credit history</h3>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Return / supplier</th>
                  <th>Original invoice</th>
                  <th>Dispatch voucher</th>
                  <th>Date</th>
                  <th>Days since dispatch</th>
                  <th>Goods</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {report.events.map((e) => (
                  <tr key={e.id}>
                    <td>
                      {e.returnNumber}
                      <span className="block text-xs text-gray-500">
                        {e.supplier}
                      </span>
                    </td>
                    <td>{e.invoiceNumber}</td>
                    <td>{e.voucherNumber}</td>
                    <td>{e.date}</td>
                    <td>{e.ageDays}</td>
                    <td>₹{e.goods}<span className="block text-xs text-gray-500">Awaiting ₹{e.awaitingGoods ?? (e.status==='CREDIT_ACCEPTED'?'0.00':e.goods)}</span>{e.disputedGoods&&e.disputedGoods!=='0.00'&&<span className="block text-xs text-amber-800">{e.supplierDecision} hold ₹{e.disputedGoods}</span>}</td>
                    <td>{e.status==='WRITTEN_OFF'?<>Approved goods loss · {e.lossVoucher}<span className="block text-xs text-gray-500">Written off ₹{e.writtenOffGoods}; not supplier credit or cash</span></>:e.status==='CREDIT_ACCEPTED'||e.status==='CREDIT_PARTIALLY_ACCEPTED'?<>{e.status==='CREDIT_ACCEPTED'?'Credit accepted':'Partially accepted'} · {e.creditNote}<span className="block text-xs text-gray-500">{e.creditVoucher}</span></>:'Awaiting supplier credit'}</td>
                  </tr>
                ))}
                {!report.events.length && (
                  <tr>
                    <td colSpan={7}>
                      No source-accounted dispatches through this date.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <button
              type="button"
              disabled={busy || report.offset === 0}
              onClick={() => load(Math.max(0, report.offset - report.limit))}
            >
              Previous dispatches
            </button>
            <span>
              {report.totalCount
                ? `${report.offset + 1}–${Math.min(report.offset + report.events.length, report.totalCount)}`
                : "0"}{" "}
              of {report.totalCount}
            </span>
            <button
              type="button"
              disabled={
                busy || report.offset + report.limit >= report.totalCount
              }
              onClick={() => load(report.offset + report.limit)}
            >
              Next dispatches
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Only linked accepted goods credits reduce awaiting value. Unlinked
            journals/openings require document review; a zero net difference
            alone does not certify settlement or tax treatment.
          </p>
        </div>
      )}
    </details>
  );
}
